import { CheckboxProps, VStack } from '@chakra-ui/react';
import {
  Checkbox,
  FieldHelpText,
  FieldTitle,
  InputVariant,
  SelectOption,
} from '@pluxee-design-system/core';
import { BaseControlProps } from '@pluxee-design-system/react-hook-form';
import ProductCard from 'common/cards/ProductCard';
import { union, without } from 'lodash';
import {
  ChangeEvent,
  ComponentType,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useController } from 'react-hook-form';

export interface CheckboxGroupControlProps extends BaseControlProps {
  options: readonly SelectOption[];
  placeholder?: ReactNode;
  renderComponent?: ComponentType<SelectOption>;
  renderLeftElement?: ComponentType<SelectOption>;
  renderRightElement?: ComponentType<SelectOption>;
  sortAlphabetically?: boolean;
  title?: string;
}

const ExtendedCheckbox = ({
  onChange,
  ...props
}: Omit<CheckboxProps, 'onChange'> & {
  disabled?: boolean;
  onChange: (id: string | number, value: boolean) => void;
}) => {
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (props.value !== undefined) {
        onChange(props.value, event.target.checked);
      }
    },
    [onChange, props.value],
  );

  return <Checkbox {...props} onChange={handleChange} />;
};
const MemoizedCheckbox = memo(ExtendedCheckbox);

export const getCheckboxGroupVariant = (
  readonly: boolean | undefined,
  isTouched: boolean,
  hasError: boolean,
  disabled: boolean | undefined,
) => {
  if (readonly) return InputVariant.READONLY;
  if (disabled) return 'disabled';
  if (hasError) return InputVariant.ERROR;
  if (isTouched) return InputVariant.SUCCESS;
  return InputVariant.FILLED;
};

// inspired by https://gist.github.com/joshsalverda/d808d92f46a7085be062b2cbde978ae6
const CheckboxGroupControl = ({
  control,
  disabled,
  id,
  mandatory,
  name,
  options: rawOptions,
  readonly,
  renderComponent: RenderComponent,
  renderLeftElement: RenderLeftElement,
  renderRightElement: RenderRightElement,
  sortAlphabetically = true,
  title,
}: CheckboxGroupControlProps) => {
  const { field, fieldState } = useController({ name, control });
  const { error, isTouched } = fieldState;
  const { onChange } = field;
  const hasError = Boolean(error);

  const fieldArray = useRef(field.value);
  const variant = getCheckboxGroupVariant(readonly, isTouched, hasError, disabled);
  const options = useMemo(
    () =>
      sortAlphabetically
        ? [...(rawOptions ?? [])].sort((a, b) => a.label?.localeCompare(b.label))
        : rawOptions,
    [rawOptions, sortAlphabetically],
  );
  const handleChange = useCallback(
    (checkboxValue: string | number, isCheckboxChecked: boolean) => {
      if (isCheckboxChecked) {
        fieldArray.current = union(fieldArray.current, [checkboxValue]);
      } else {
        fieldArray.current = without(fieldArray.current, checkboxValue);
      }
      onChange(fieldArray.current);
    },
    [onChange],
  );

  useEffect(() => {
    fieldArray.current = field.value;
  }, [field.value]);

  return (
    <>
      {title && (
        <FieldTitle
          title={title}
          mandatory={Boolean(mandatory)}
          disabled={variant === 'disabled'}
          variant={variant !== 'disabled' ? variant : undefined}
        />
      )}
      <VStack spacing={4} align="stretch">
        {options.map((option, index) => (
          <ProductCard
            key={option.value}
            variant={
              (variant === InputVariant.FILLED || variant === InputVariant.SUCCESS) &&
              field.value.includes(option.value)
                ? 'selected'
                : variant
            }
          >
            <MemoizedCheckbox
              id={`${id || name}.${index}`}
              name={name}
              isChecked={field.value.includes(option.value)}
              isDisabled={variant === 'disabled' || option.isDisabled}
              isInvalid={variant === InputVariant.ERROR}
              isReadOnly={variant === InputVariant.READONLY}
              value={option.value}
              onChange={handleChange}
              // mt={RenderComponent ? 3.5 : undefined}
            >
              {RenderLeftElement && <RenderLeftElement {...option} />}
              {RenderComponent ? undefined : option.label}
            </MemoizedCheckbox>
            {RenderComponent && <RenderComponent {...option} />}
            {RenderRightElement && <RenderRightElement {...option} />}
          </ProductCard>
        ))}
      </VStack>
      {hasError && error?.message && (
        <FieldHelpText
          disabled={variant === 'disabled'}
          text={error.message}
          variant={InputVariant.ERROR}
        />
      )}
    </>
  );
};

export default CheckboxGroupControl;
